import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPencil,
  faTrash,
  faDollarSign,
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';

const WashingCostsManagement = () => {
  const [washingCosts, setWashingCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [formData, setFormData] = useState({
    procedure_name: '',
    cost_per_unit_cad: '',
    currency: 'CAD',
    unit_of_measure: 'sqm',
    year: 2024,
    sort_order: 99.0,
    is_default: false,
    is_active: true
  });
  const [convertedCost, setConvertedCost] = useState(null);
  const [defaultSystemSettings, setDefaultSystemSettings] = useState({ currency: 'CAD', uom: 'sqm' });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState('');

  // Fetch default system settings
  useEffect(() => {
    const fetchDefaultSettings = async () => {
      try {
        const settingsResponse = await api.get('/settings/categories');
        if (settingsResponse.data.success) {
          const settings = settingsResponse.data.data;
          const currency = settings.currency_units?.currency?.setting_value || 'CAD';
          const preferredUnits = settings.currency_units?.preferred_units?.setting_value || 'm²';
          const uom = preferredUnits.toLowerCase().includes('m') ? 'sqm' : 'sqft';
          setDefaultSystemSettings({ currency, uom });
        }
      } catch (err) {
        console.error('Error fetching default settings:', err);
      }
    };
    fetchDefaultSettings();
  }, []);

  // Calculate converted cost when form data changes
  useEffect(() => {
    const calculateConvertedCost = async () => {
      if (!formData.cost_per_unit_cad || !formData.currency || !formData.unit_of_measure || !formData.year) {
        setConvertedCost(null);
        return;
      }

      try {
        // Get conversion rate for the year
        let currencyRate = 1.0;
        if (formData.currency !== defaultSystemSettings.currency) {
          const rateResponse = await api.get(`/currency-conversion-rates/year/${formData.year}/${formData.currency}/${defaultSystemSettings.currency}`);
          if (rateResponse.data.success) {
            currencyRate = parseFloat(rateResponse.data.data.conversionRate);
          }
        }

        // Convert currency
        let converted = parseFloat(formData.cost_per_unit_cad) * currencyRate;

        // Convert UOM (1 sqft = 0.092903 sqm)
        const SQFT_TO_SQM = 0.092903;
        if (formData.unit_of_measure === 'sqft' && defaultSystemSettings.uom === 'sqm') {
          converted = converted * SQFT_TO_SQM;
        } else if (formData.unit_of_measure === 'sqm' && defaultSystemSettings.uom === 'sqft') {
          converted = converted / SQFT_TO_SQM;
        }

        setConvertedCost(converted.toFixed(2));
      } catch (err) {
        console.error('Error calculating converted cost:', err);
        setConvertedCost(null);
      }
    };

    calculateConvertedCost();
  }, [formData.cost_per_unit_cad, formData.currency, formData.unit_of_measure, formData.year, defaultSystemSettings]);

  // Fetch washing costs
  const fetchWashingCosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(yearFilter && { year: yearFilter }),
        
      });

      const response = await api.get(`/washing-costs?${params}`);
      if (response.data.success) {
        setWashingCosts(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError('Failed to fetch washing costs');
      console.error('Error fetching washing costs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWashingCosts();
  }, [pagination.page, searchTerm, yearFilter]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCost) {
        await api.put(`/washing-costs/${editingCost.id}`, formData);
      } else {
        await api.post('/washing-costs', formData);
      }
      
      setShowModal(false);
      setEditingCost(null);
      resetForm();
      fetchWashingCosts();
    } catch (err) {
      setError('Failed to save washing cost');
      console.error('Error saving washing cost:', err);
    }
  };

  // Handle edit
  const handleEdit = (cost) => {
    setEditingCost(cost);
    setFormData({
      procedure_name: cost.procedureName,
      cost_per_unit_cad: cost.costPerUnitCad,
      currency: cost.currency,
      unit_of_measure: cost.unitOfMeasure,
      year: cost.year,
      sort_order: cost.sortOrder || 99.0,
      is_default: cost.isDefault || false,
      is_active: cost.isActive !== undefined ? cost.isActive : true
    });
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this washing cost?')) {
      try {
        await api.delete(`/washing-costs/${id}`);
        fetchWashingCosts();
      } catch (err) {
        setError('Failed to delete washing cost');
        console.error('Error deleting washing cost:', err);
      }
    }
  };

  

  // Reset form
  const resetForm = () => {
    setFormData({
      procedure_name: '',
      cost_per_unit_cad: '',
      currency: 'CAD',
      unit_of_measure: defaultSystemSettings.uom,
      year: 2024,
      sort_order: 99.0,
      is_default: false,
      is_active: true
    });
    setEditingCost(null);
    setConvertedCost(null);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Washing Costs Management</h1>
        <p className="text-gray-600">Manage washing procedures and their costs for bridge maintenance</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search washing procedures..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Years</option>
          <option value="2024">2024</option>
          <option value="2025">2025</option>
        </select>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
          Add Washing Cost
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading washing costs...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Procedure Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Currency / UOM
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sort Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Year
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {washingCosts.map((cost) => (
                  <tr key={cost.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          cost.procedureName === 'Default no Wash' 
                            ? 'bg-gray-100 text-gray-800'
                            : cost.procedureName === 'Wash'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {cost.procedureName}
                        </span>
                        {cost.isDefault && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                            Default
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faDollarSign} className="h-4 w-4 text-green-600 mr-1" />
                        <span className="font-medium">{parseFloat(cost.costPerUnitCad || 0).toFixed(2)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {cost.currency} / {cost.unitOfMeasure || 'sqm'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {cost.sortOrder || 99.0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {cost.year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(cost)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faPencil} className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(cost.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(pagination.page - 1) * pagination.limit + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setPagination({ ...pagination, page })}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pagination.page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingCost ? 'Edit Washing Cost' : 'Add New Washing Cost'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Procedure Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.procedure_name}
                    onChange={(e) => setFormData({ ...formData, procedure_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Wash, Hi Pressure/Steam Clean"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cost per Unit *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.cost_per_unit_cad}
                    onChange={(e) => setFormData({ ...formData, cost_per_unit_cad: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency *
                  </label>
                  <select
                    required
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CAD">CAD</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit of Measure *
                  </label>
                  <select
                    required
                    value={formData.unit_of_measure}
                    onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sqft">sqft (Square Feet)</option>
                    <option value="sqm">sqm (Square Meters)</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Converted Cost ({defaultSystemSettings.currency}/{defaultSystemSettings.uom}) <span className="text-xs text-blue-600">(Read-only)</span>
                  </label>
                  <input
                    type="text"
                    value={convertedCost || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                    placeholder="Auto-calculated"
                  />
                  <p className="text-xs text-gray-500 mt-1">Automatically converted to default system currency and UOM</p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort Order *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    required
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseFloat(e.target.value) || 99.0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 1.0, 1.5, 2.0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first. Decimals allowed for flexible ordering.</p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Year *
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) || 2024 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="2024"
                  />
                </div>
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Set as Default Procedure</label>
                  <p className="text-xs text-gray-500 ml-2">(Only one procedure can be default)</p>
                </div>
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Active</label>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingCost ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WashingCostsManagement;
